import {
  IStateContextData,
  QUIZ_STEPS,
} from '@src/interfaces/state.interfaces';

import { QUIZ_STEPS_HISTORY } from './navigation.constants';
import { GENDER, QUESTIONS_CONSTANTS } from './questions.constants';

export const DEFAULT_FINAL_BODY_FAT = '6-14%';

export const PROGRESS_TEASER = {
  SLIDE_DURATION: 1500,
  SLIDE_TRANSITION_DURATION: 0.5,
  SLIDE_OFFSET: 640,
  COUNTER_DURATION: 1,
  COUNTER_FROM: 17_000,
  COUNTER_TO: 17_500,
  WEEKS_TO_FEEL: 1,
  WEEKS_TO_SEE: 2,
  WEEKS_TO_NOTICE: 3,
} as const;

export const PROGRESS_TEASER_SLIDE = {
  WELCOME: 'progressTeaser.welcome',
  MINUTES: 'progressTeaser.minutes',
  FEEL: 'progressTeaser.feel',
  SEE: 'progressTeaser.see',
  NOTICE: 'progressTeaser.notice',
  JOINED: 'progressTeaser.joined',
  START: 'progressTeaser.start',
} as const;

export type IProgressTeaserSlide =
  (typeof PROGRESS_TEASER_SLIDE)[keyof typeof PROGRESS_TEASER_SLIDE];

export const PROGRESS_TEASER_SLIDES = Object.values(PROGRESS_TEASER_SLIDE);

export const BMI_STATUS = {
  NORMAL: 'normal',
  OVERWEIGHT: 'overweight',
  OBESE: 'obese',
} as const;

export const TAG_TYPE = {
  INFO: 'info',
  REALISTIC: 'realistic',
  EASY: 'easy',
  HEALTH: 'health',
  CHALLENGING: 'challenging',
} as const;

export type IBmiStatus = (typeof BMI_STATUS)[keyof typeof BMI_STATUS];
export type ITagType = (typeof TAG_TYPE)[keyof typeof TAG_TYPE];

export const INITIAL_DATA: IStateContextData = {
  stepIndex: 0,
  variant: null,
  answers: {},

  // Social
  personAreas: {
    chest: false,
    glutes: false,
    arms: false,
    abs: false,
    thighs: false,
    legs: false,
  },
  event_date: undefined,
  email: undefined,

  // Steps info
  age: null,
  intermittentFasting: null,
  achieve: null,
  gender: null,
  current_body_type: null,
  ideal_body_type: null,
  last_happy: null,
  feel_hungry: null,
  time_breakfast: null,
  time_lunch: null,
  time_dinner: null,
  prepare_meals: null,
  fasting_weekends: null,
  dietary_pattern: null,
  sleep: null,
  physically_active: null,
  working_hours: null,
  stairs_breath: null,
  walking_typical_day: null,
  daily_meals: null,
  drink_water: null,
  bad_habits: null,
  lifestyle: null,
  achieve_your_target_weight: null,
  height: null,
  currentWeight: null,
  targetWeight: null,
  yourAge: undefined,
  lose_weight_for: null,
};

export const MOCK_QUIZ_GENDER = GENDER.MALE;
export const MOCK_QUIZ_DATA = {
  stepIndex: QUIZ_STEPS_HISTORY.findIndex(
    ({ step }) => step === QUIZ_STEPS.EMAIL,
  ),
  personAreas: {
    chest: false,
    glutes: true,
    arms: true,
    abs: true,
    thighs: false,
    legs: true,
  },
  yourAge: 35,
  intermittentFasting: QUESTIONS_CONSTANTS.intermittentFasting[0].value,
  achieve: QUESTIONS_CONSTANTS.achieve.slice(0, 2).map(({ value }) => value),
  gender: MOCK_QUIZ_GENDER,
  current_body_type:
    QUESTIONS_CONSTANTS.current_body_type(MOCK_QUIZ_GENDER)[0].value,
  ideal_body_type:
    QUESTIONS_CONSTANTS.ideal_body_type(MOCK_QUIZ_GENDER)[0].value,
  last_happy: QUESTIONS_CONSTANTS.last_happy[0].value,
  feel_hungry: QUESTIONS_CONSTANTS.feel_hungry[0].value,
  time_breakfast: QUESTIONS_CONSTANTS.time_breakfast[0].value,
  time_lunch: QUESTIONS_CONSTANTS.time_lunch[0].value,
  time_dinner: QUESTIONS_CONSTANTS.time_dinner[0].value,
  prepare_meals: QUESTIONS_CONSTANTS.prepare_meals[0].value,
  fasting_weekends: QUESTIONS_CONSTANTS.fasting_weekends[0].value,
  dietary_pattern: QUESTIONS_CONSTANTS.dietary_pattern[0].value,
  sleep: QUESTIONS_CONSTANTS.sleep[0].value,
  physically_active: QUESTIONS_CONSTANTS.physically_active[0].value,
  working_hours: QUESTIONS_CONSTANTS.working_hours[0].value,
  stairs_breath: QUESTIONS_CONSTANTS.stairs_breath[0].value,
  walking_typical_day: QUESTIONS_CONSTANTS.walking_typical_day[0].value,
  daily_meals: QUESTIONS_CONSTANTS.daily_meals[0].value,
  drink_water: 9,
  bad_habits: QUESTIONS_CONSTANTS.bad_habits
    .slice(1, 3)
    .map(({ value }) => value),
  lifestyle: QUESTIONS_CONSTANTS.lifestyle[0].value,
  achieve_your_target_weight:
    QUESTIONS_CONSTANTS.achieve_your_target_weight[0].value,
  height: {
    cm: 200,
    isMetric: true,
  },
  currentWeight: {
    lbs: undefined,
    kg: 200,
    isMetric: true,
  },
  targetWeight: {
    lbs: undefined,
    kg: 50,
    isMetric: true,
  },
  lose_weight_for: null,
};

export const MOCK_QUIZ_DATA_WITH_ERRORS = {
  stepIndex: QUIZ_STEPS_HISTORY.findIndex(
    ({ step }) => step === QUIZ_STEPS.EMAIL,
  ),
  personAreas: {
    chest: false,
    glutes: true,
    arms: true,
    abs: true,
    thighs: false,
    legs: true,
  },
  age: null,
  intermittentFasting: QUESTIONS_CONSTANTS.intermittentFasting[0].value,
  achieve: QUESTIONS_CONSTANTS.achieve.slice(0, 2).map(({ value }) => value),
  gender: MOCK_QUIZ_GENDER,
  current_body_type:
    QUESTIONS_CONSTANTS.current_body_type(MOCK_QUIZ_GENDER)[0].value,
  ideal_body_type:
    QUESTIONS_CONSTANTS.ideal_body_type(MOCK_QUIZ_GENDER)[0].value,
  last_happy: QUESTIONS_CONSTANTS.last_happy[0].value,
  feel_hungry: null,
  time_breakfast: QUESTIONS_CONSTANTS.time_breakfast[0].value,
  time_lunch: null,
  time_dinner: null,
  prepare_meals: QUESTIONS_CONSTANTS.prepare_meals[0].value,
  fasting_weekends: QUESTIONS_CONSTANTS.fasting_weekends[0].value,
  dietary_pattern: QUESTIONS_CONSTANTS.dietary_pattern[0].value,
  sleep: QUESTIONS_CONSTANTS.sleep[0].value,
  physically_active: null,
  working_hours: QUESTIONS_CONSTANTS.working_hours[0].value,
  stairs_breath: QUESTIONS_CONSTANTS.stairs_breath[0].value,
  walking_typical_day: QUESTIONS_CONSTANTS.walking_typical_day[0].value,
  daily_meals: QUESTIONS_CONSTANTS.daily_meals[0].value,
  drink_water: 9,
  bad_habits: QUESTIONS_CONSTANTS.bad_habits
    .slice(1, 3)
    .map(({ value }) => value),
  lifestyle: QUESTIONS_CONSTANTS.lifestyle[0].value,
  achieve_your_target_weight:
    QUESTIONS_CONSTANTS.achieve_your_target_weight[0].value,
  height: null,
  currentWeight: null,
  targetWeight: null,
  lose_weight_for: null,
};
